import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface EndpointsProps {
  apiUrl: string;
}

interface EndpointSummary {
  device_id: string;
  hostname: string | null;
  os: string | null;
  status: string;
  enforcement: string;
  last_seen: string | null;
  enrolled_at: string | null;
  dlp_violations: number;
  last_violation_at: string | null;
  ai_usage_count: number;
  shadow_ai_count: number;
  managed_ai_count: number;
  ai_apps: string[];
  users: string[];
}

type Filter = 'all' | 'violations' | 'shadow';

const lastActivity = (e: EndpointSummary): string | null => {
  const dates = [e.last_violation_at, e.last_seen].filter(Boolean) as string[];
  if (!dates.length) return null;
  return dates.reduce((a, b) => (new Date(a).getTime() > new Date(b).getTime() ? a : b));
};

const Endpoints: React.FC<EndpointsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [endpoints, setEndpoints] = useState<EndpointSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    try {
      const data = await fetch(`${apiUrl}/admin/bdr/endpoints-summary`).then((r) => r.json());
      setEndpoints(Array.isArray(data.endpoints) ? data.endpoints : []);
    } catch (err) {
      console.error('Failed to fetch endpoint summary:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const withViolations = endpoints.filter((e) => e.dlp_violations > 0).length;
  const withShadowAi = endpoints.filter((e) => e.shadow_ai_count > 0).length;
  const totalViolations = endpoints.reduce((sum, e) => sum + e.dlp_violations, 0);
  const totalUsage = endpoints.reduce((sum, e) => sum + e.ai_usage_count, 0);

  const filtered = endpoints.filter((e) => {
    if (filter === 'violations') return e.dlp_violations > 0;
    if (filter === 'shadow') return e.shadow_ai_count > 0;
    return true;
  });

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="signal" size={20} /> {t('pages.endpoints.endpoints')}
          </h1>
          <p className="subtitle">
            {t('pages.endpoints.dlpViolationsAndShadowAIUsage')}
          </p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <button className="ent-btn" onClick={load} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} /> {t('pages.endpoints.refresh')}
          </button>
        </div>
      </div>

      <div className="metric-row">
        <div className="metric accent-info">
          <span className="label">{t('pages.endpoints.endpoints')}</span>
          <div className="value">{endpoints.length}</div>
        </div>
        <div className="metric accent-danger">
          <span className="label">{t('pages.endpoints.withDLPViolations')}</span>
          <div className="value">{withViolations}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">{t('pages.endpoints.withShadowAI')}</span>
          <div className="value">{withShadowAi}</div>
        </div>
        <div className="metric accent-danger">
          <span className="label">{t('pages.endpoints.totalViolations')}</span>
          <div className="value">{totalViolations}</div>
        </div>
        <div className="metric accent-info">
          <span className="label">{t('pages.endpoints.totalAIUsage')}</span>
          <div className="value">{totalUsage}</div>
        </div>
      </div>

      <div className="notice info" style={{ marginBottom: 16 }}>
        <Icon name="info" />
        <span>
          <strong>{t('pages.endpoints.users')}</strong> {t('pages.endpoints.showsTheOSUsernameSObserved')}
        </span>
      </div>

      <div className="ent-toolbar">
        {(['all', 'violations', 'shadow'] as const).map((f) => (
          <button
            key={f}
            className={`ent-btn ${filter === f ? 'primary' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('pages.endpoints.allEndpoints') : f === 'violations' ? t('pages.endpoints.hasViolations') : t('pages.endpoints.shadowAI')}
          </button>
        ))}
      </div>

      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th style={{ width: 200 }}>{t('pages.endpoints.endpoint')}</th>
              <th style={{ width: 80 }}>{t('pages.endpoints.os')}</th>
              <th style={{ width: 140 }}>{t('pages.endpoints.userS')}</th>
              <th style={{ width: 110 }}>{t('pages.endpoints.dlpViolations')}</th>
              <th style={{ width: 130 }}>{t('pages.endpoints.aiUsage')}</th>
              <th>{t('pages.endpoints.aiApps')}</th>
              <th style={{ width: 150 }}>{t('pages.endpoints.lastActivity')}</th>
              <th style={{ width: 90 }}>{t('pages.endpoints.status')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && endpoints.length === 0 ? (
              <tr>
                <td colSpan={8} className="ent-empty">{t('pages.endpoints.loading')}</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="ent-empty">{t('pages.endpoints.noEndpointsMatchThisFilter')}</td>
              </tr>
            ) : (
              filtered.map((e) => {
                const activity = lastActivity(e);
                return (
                  <tr key={e.device_id}>
                    <td>
                      <strong>{e.hostname || e.device_id}</strong>
                      {e.hostname && (
                        <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2, fontFamily: 'monospace' }}>
                          {e.device_id}
                        </div>
                      )}
                    </td>
                    <td>{e.os || '—'}</td>
                    <td title={e.users.join(', ')}>
                      {e.users.length ? e.users.slice(0, 2).join(', ') + (e.users.length > 2 ? ` +${e.users.length - 2}` : '') : '—'}
                    </td>
                    <td>
                      {e.dlp_violations > 0 ? (
                        <span className="badge badge-error">{e.dlp_violations}</span>
                      ) : (
                        <span style={{ color: 'var(--text-2)' }}>0</span>
                      )}
                    </td>
                    <td>
                      {e.ai_usage_count > 0 ? (
                        <>
                          <span className="badge badge-info">{e.ai_usage_count}</span>
                          {e.shadow_ai_count > 0 && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-2)' }}>{t('pages.endpoints.shadow', { v0: e.shadow_ai_count })}</span>
                          )}
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-2)' }}>0</span>
                      )}
                    </td>
                    <td>
                      {e.ai_apps.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {e.ai_apps.slice(0, 4).map((app) => (
                            <span key={app} className="badge badge-unknown" style={{ fontSize: 10 }}>
                              {app}
                            </span>
                          ))}
                          {e.ai_apps.length > 4 && (
                            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{t('pages.endpoints.more', { v0: e.ai_apps.length - 4 })}</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-2)' }}>—</span>
                      )}
                    </td>
                    <td>{activity ? parseServerDate(activity).toLocaleString() : '—'}</td>
                    <td>
                      <span className={`badge ${e.status === 'active' ? 'badge-success' : e.status === 'revoked' ? 'badge-error' : 'badge-unknown'}`}>
                        {e.status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Endpoints;
